import { MetricsService, InvalidMetricsException } from '@src/metrics';
import { EnvironmentDetail } from '@src/utils';
import _ from 'lodash';

/**
 *  The MetricsBatcher class is used to batch the outgoing metrics.
 */
export class MetricsBatcher {
    private static readonly MAX_QUEUE_SIZE = 200;
    private static readonly MAX_QUEUE_SIZE_BEFORE_WARNING = 100;
    private static readonly BATCH_SIZE = 30;
    private static readonly MAX_RETRY_ATTEMPTS = 2;
    private static readonly THROTTLING_LIMIT = 60; // per TIME_BETWEEN_CALLS
    private static readonly TIME_BETWEEN_CALLS = 2000; // ms
    private static readonly RETRY_BACKOFF_CAP = 10000; // maximum backoff is 10s
    private static readonly RETRY_BACKOFF_BASE = 2500; // first retry in 5s, second in 10s
    private static decoratedMetricsQueue = [];
    private static throttlingMap = new Map();

    private static async processMetricPublishTask(decoratedMetrics) {
        const retryableMetrics = [];
        decoratedMetrics.forEach((decoratedMetric) => {
            if (decoratedMetric.publishAttempts <= MetricsBatcher.MAX_RETRY_ATTEMPTS) {
                decoratedMetric.publishAttempts++;

                // one metric operation/method can have multiple metric names
                // in this approach, we throttle by method name instead of metric name,
                // but can be easily changed if more granular throttling is desired
                const key = decoratedMetric.metricObj.method;
                MetricsBatcher.throttlingMap.set(key, (MetricsBatcher.throttlingMap.get(key) ?? 0) + 1);

                if (MetricsBatcher.throttlingMap.get(key) < MetricsBatcher.THROTTLING_LIMIT) {
                    retryableMetrics.push(decoratedMetric);
                } else {
                    console.error(`Could not publish metric ${JSON.stringify(decoratedMetric.metricObj)} because
                     "${key}" has exceeded the throttling limit of ${MetricsBatcher.THROTTLING_LIMIT} calls.`);
                }
            } else {
                console.error(
                    `Could not publish metric ${JSON.stringify(decoratedMetric.metricObj)} because it has already been retried ${MetricsBatcher.MAX_RETRY_ATTEMPTS} times.`,
                );
            }
        });

        if (retryableMetrics.length === 0) {
            return;
        }

        // Un-decorate the metrics and publish them
        const undecoratedMetrics = retryableMetrics.map((retryableMetric) => {
            return retryableMetric.metricObj;
        });

        try {
            await MetricsService.publishMetric(undecoratedMetrics);
        } catch (error) {
            if (error instanceof InvalidMetricsException) {
                console.error(`Following error occurred while publishing metrics: ${error.getMessage()}. Will not retry because the retries will fail as well.`);
            } else {
                if (typeof error === 'string' && error.startsWith('Disabled.')) {
                    console.error(`Publishing metrics is disabled. Will not retry.`);
                } else if (MetricsBatcher.decoratedMetricsQueue.length >= MetricsBatcher.MAX_QUEUE_SIZE) {
                    console.error(
                        `Could not publish metrics due to error ${JSON.stringify(error)}. Will not reprocess this since there are already more than ${this.decoratedMetricsQueue.length} metrics remaining to process`,
                    );
                } else {
                    console.info(`Publishing metrics failed: ${error.message}. Will retry.`);
                    // Exponential backoff policy with jitter
                    const backoff = Math.min(MetricsBatcher.RETRY_BACKOFF_CAP, MetricsBatcher.RETRY_BACKOFF_BASE * 2 ** retryableMetrics[0].publishAttempts);
                    const jitter = _.random(0, backoff);
                    setTimeout(() => MetricsBatcher.decoratedMetricsQueue.unshift(...retryableMetrics), jitter);
                }
            }
        }
    }

    /**
     * This method adds a retry count to metrics object so that we can track how many times a metric
     * has been retried
     */
    private static decorateAndAddToQueue(metricObj) {
        // Removed decoration for Bees since RecordMetrics will return error anyway.
        const newMetric = {
            method: metricObj.operation,
            metric: metricObj.name,
            startTime: metricObj.startTime,
            value: metricObj.value,
            clientProgram: metricObj.clientProgram,
            dimensions: metricObj.dimensions,
            program: metricObj.program,
            type: '',
        };
        if (metricObj.type.toLowerCase().includes('latency')) {
            newMetric.type = 'LATENCY';
        } else {
            newMetric.type = 'COUNT';
        }
        const decoratedMetricObjForIronHide = {
            metricObj: newMetric,
            publishAttempts: 0,
        };
        return MetricsBatcher.decoratedMetricsQueue.push(decoratedMetricObjForIronHide);
    }

    public static sendMetric(metricObj) {
        // Check if running in Android
        if (EnvironmentDetail.isAndroid()) {
            return MetricsBatcher.decorateAndAddToQueue(metricObj);
        }
        // Else not running on mobile. Therefore, will not publish metrics.
    }

    public static registerInterval() {
        window.setInterval(() => {
            if (MetricsBatcher.decoratedMetricsQueue.length > MetricsBatcher.MAX_QUEUE_SIZE_BEFORE_WARNING) {
                console.warn(`Metrics queue size is now ${MetricsBatcher.decoratedMetricsQueue.length}`);
            }
            while (MetricsBatcher.decoratedMetricsQueue.length >= MetricsBatcher.BATCH_SIZE) {
                MetricsBatcher.processMetricPublishTask(MetricsBatcher.decoratedMetricsQueue.splice(0, MetricsBatcher.BATCH_SIZE));
            }

            MetricsBatcher.throttlingMap.clear();
        }, MetricsBatcher.TIME_BETWEEN_CALLS);
    }
}
